import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cx } from "./cx";
import { Icon, type IconName } from "./Icon";

type ButtonVariant = "plain" | "p" | "soft" | "q" | "goodb";
type ButtonSize = "s" | "md" | "lg" | "xl";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** plain: the white button; p: the wine one; soft: wine-tinted; q: words only; goodb: a quiet green one. */
  variant?: ButtonVariant;
  size?: ButtonSize;
  full?: boolean;
  /** An icon before the words. */
  icon?: IconName;
  /** An icon after the words, such as an arrow. */
  after?: IconName;
  children?: ReactNode;
}

/** The kit's button (`.btn`). It is a real `<button type="button">` unless told otherwise. */
export function Button({ variant = "plain", size = "md", full = false, icon, after, className, children, type = "button", ...rest }: ButtonProps) {
  return (
    <button
      type={type}
      className={cx("btn", variant !== "plain" && variant, size !== "md" && size, full && "full", className)}
      {...rest}
    >
      {icon ? <Icon name={icon} /> : null}
      {children}
      {after ? <Icon name={after} /> : null}
    </button>
  );
}

interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  icon: IconName;
  /** What the button does, read aloud and shown as its tooltip: an icon alone says nothing. */
  label: string;
  small?: boolean;
  /** Drawn with a border, for a button that stands alone on the ground. */
  bordered?: boolean;
}

/** A square button holding one icon (`.ibtn`). */
export function IconButton({ icon, label, small = false, bordered = false, className, type = "button", ...rest }: IconButtonProps) {
  return (
    <button type={type} className={cx("ibtn", small && "sm", bordered && "bd", className)} aria-label={label} title={label} {...rest}>
      <Icon name={icon} />
    </button>
  );
}
